<template>
  <div class="multiselect-container" :class="dropdownAlignment">
    <div class="trigger-wrapper">
      <!-- Trigger button -->
      <n-tooltip position="left" :text="tooltip">
        <button class="button" @click="isOpen = !isOpen">
          <slot name="button-display" />
        </button>
      </n-tooltip>

      <!-- Dropdown menu -->
      <div v-if="isOpen" class="dropdown-menu">
        <div
          v-for="option in options"
          :key="getIdValue(option)"
          class="option__content"
          :class="{ 'is-selected': isOptionSelected(option), 'is-hover': isOptionSelectedHover(option) }"
          @mouseover="onHover(option)"
          @mouseleave="hoveredOptionId = null"
        >
          <input
            type="checkbox"
            :id="getIdValue(option)"
            :checked="isOptionSelected(option)"
            :disabled="isLastOptionSelected(option)"
            @change="(e) => onSelectionChanged(option, (e.target as HTMLInputElement).checked)"
          />
          <label :for="getIdValue(option)" class="option__title">
            {{ getDisplayLabel(option) }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted, PropType } from 'vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    required: true,
  },
  idAttribute: {
    type: String,
    required: true,
  },
  displayAttribute: {
    type: String,
    required: true,
  },
  tooltip: {
    type: String,
    default: '',
  },
  dropdownAlignment: {
    type: String as PropType<'left' | 'right'>,
    default: 'right',
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
}>();

const selectedIds = ref<string[]>([]);
const isOpen = ref(false);
const hoveredOptionId = ref<string | null>(null);

onMounted(() => {
  selectedIds.value = props.modelValue.map((o: any) => getIdValue(o));
});

watch(selectedIds, (newIds: string[]) => {
  const fullValues = props.options.filter((o: any) => newIds.includes(getIdValue(o)));
  emit('update:modelValue', fullValues);
});

// Close dropdown on outside click
const closeOnOutsideClick = (event: MouseEvent) => {
  const trigger = document.querySelector('.trigger-wrapper');
  if (trigger && !trigger.contains(event.target as Node)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', closeOnOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener('click', closeOnOutsideClick);
});

function isOptionSelected(option: any) {
  return selectedIds.value.some((id: string) => id === getIdValue(option));
}

function isOptionSelectedHover(option: any) {
  return getIdValue(option) === hoveredOptionId.value;
}

function onHover(option: any) {
  hoveredOptionId.value = getIdValue(option);
}

function isLastOptionSelected(option: any) {
  return isOptionSelected(option) && selectedIds.value.length === 1;
}

function getIdValue(option: any) {
  return option[props.idAttribute];
}

function getDisplayLabel(option: any) {
  return option[props.displayAttribute];
}

function onSelectionChanged(option: any, checked: boolean) {
  const affectedId = getIdValue(option);
  let newIdValues = [...selectedIds.value];

  if (checked) {
    if (affectedId && !newIdValues.some((id) => id === affectedId)) {
      newIdValues.push(affectedId);
    }
  } else {
    newIdValues = newIdValues.filter((id: string) => id !== affectedId);
  }
  // Enforce at least one option selected (equivalent to allow-empty="false")
  if (newIdValues.length === 0 && props.options.length > 0) {
    newIdValues = [getIdValue(props.options[0])];
  }
  selectedIds.value = newIdValues;
}
</script>

<style scoped lang="scss">
.multiselect-container {
  display: flex;
  justify-content: flex-end;

  &.left .dropdown-menu {
    left: 0; /* Top-left of menu aligns with bottom-left of button */
    right: unset;
  }

  &.right .dropdown-menu {
    right: 0; /* Top-right of menu aligns with bottom-right of button */
    left: unset;
  }
}

.trigger-wrapper {
  position: relative; /* Position the dropdown relative to this wrapper */
}

.button {
  border-radius: 4px;
  background-color: #eee;
  border: solid 1px #eee;
  padding: 5px 10px;
  cursor: pointer;

  &:hover {
    background-color: #ccc;
    border: solid 1px #ccc;
  }

  &:focus {
    border-color: #eee;
    outline: none;
  }

  &.disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.dropdown-menu {
  position: absolute;
  display: block;
  top: calc(100% + 5px); /* Position below the button with a small gap */
  z-index: 100;
  background: white;
  border-radius: 4px;
  box-shadow:
    0 2px 3px rgba(10, 10, 10, 0.1),
    0 0 0 1px rgba(10, 10, 10, 0.1);
  min-width: 180px;
  max-height: 300px;
  overflow-y: auto;
  font-size: 0.875rem;
  padding: 5px 0;
}

.option__content {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;

  &.is-hover {
    background: whitesmoke;
  }

  &.is-selected {
    font-weight: 700;
    color: #333; /* Adjust color as needed */
  }
}

.option__title {
  margin-left: 10px;
  cursor: pointer;
}
</style>
